import React, { useState } from 'react';
import clsx from 'clsx';
import { Sparkles, Lock } from 'lucide-react';
import {
    AISuggestedPrompt,
    AICategoryType,
    AI_CATEGORIES,
} from '../../constants/aiAssistantPrompts';

// Specialized agents configuration
const AI_AGENTS = [
    { id: 'incident', name: 'Incident Analyzer', desc: 'Detects patterns in FRACAS data', icon: <Activity size={18} />, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    { id: 'rca', name: 'Root Cause Detective', desc: 'Validates 5-Why & Ishikawa logic', icon: <Search size={18} />, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    { id: '8d', name: '8D Coach', desc: 'Guides D0-D8 process step-by-step', icon: <Shield size={18} />, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    { id: 'lab', name: 'Lab Intel', desc: 'Interprets complex test results', icon: <Beaker size={18} />, color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
];

import { Activity, Search, Shield, Beaker, FileCode, GitBranch, TrendingUp } from 'lucide-react';

// Blue Sky features configuration
const BLUE_SKY_FEATURES = [
    { id: 'cross', name: 'Cross-Module Intelligence', desc: 'Trace FRACAS failure to DFMEA param', tag: 'The Holy Grail', icon: <GitBranch size={18} /> },
    { id: 'predict', name: 'Predictive Reliability', desc: 'Forecast warranty & spares volume', tag: 'Forecasting', icon: <TrendingUp size={18} /> },
    { id: 'auto', name: 'Lab Auto-Interpretation', desc: 'Analyze raw CSV & flag deviations', tag: 'Auto-Analysis', icon: <FileCode size={18} /> },
];

interface AIToolkitSidebarProps {
    isDark: boolean;
    selectedCategory: AICategoryType;
    setSelectedCategory: (cat: AICategoryType) => void;
    filteredPrompts: AISuggestedPrompt[];
    handlePromptClick: (prompt: AISuggestedPrompt) => void;
    allPrompts: AISuggestedPrompt[];
}

export const AIToolkitSidebar: React.FC<AIToolkitSidebarProps> = ({
    isDark,
    selectedCategory,
    setSelectedCategory,
    filteredPrompts,
    handlePromptClick,
    allPrompts
}) => {
    const [activeTab, setActiveTab] = useState<'prompts' | 'agents' | 'vision'>('prompts');

    return (
        <div className={clsx(
            'w-80 rounded-xl border flex flex-col overflow-hidden',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
        )}>
            {/* Sidebar Header & Tabs */}
            <div className={clsx(
                'border-b',
                isDark ? 'border-slate-700 bg-slate-900/50' : 'bg-slate-50 border-slate-200'
            )}>
                <div className="flex">
                    <button
                        onClick={() => setActiveTab('prompts')}
                        className={clsx(
                            'flex-1 py-3 text-sm font-medium transition-colors border-b-2',
                            activeTab === 'prompts'
                                ? (isDark ? 'border-purple-500 text-white' : 'border-purple-600 text-purple-900')
                                : (isDark ? 'border-transparent text-slate-400 hover:text-white' : 'border-transparent text-slate-500 hover:text-slate-700')
                        )}
                    >
                        Prompts
                    </button>
                    <button
                        onClick={() => setActiveTab('agents')}
                        className={clsx(
                            'flex-1 py-3 text-sm font-medium transition-colors border-b-2',
                            activeTab === 'agents'
                                ? (isDark ? 'border-blue-500 text-white' : 'border-blue-600 text-blue-900')
                                : (isDark ? 'border-transparent text-slate-400 hover:text-white' : 'border-transparent text-slate-500 hover:text-slate-700')
                        )}
                    >
                        Agents
                    </button>
                    <button
                        onClick={() => setActiveTab('vision')}
                        className={clsx(
                            'flex-1 py-3 text-sm font-medium transition-colors border-b-2',
                            activeTab === 'vision'
                                ? (isDark ? 'border-amber-500 text-white' : 'border-amber-600 text-amber-900')
                                : (isDark ? 'border-transparent text-slate-400 hover:text-white' : 'border-transparent text-slate-500 hover:text-slate-700')
                        )}
                    >
                        Vision
                    </button>
                </div>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto p-3">
                {/* PROMPTS TAB */}
                {activeTab === 'prompts' && (
                    <div className="space-y-4">
                        <div className="space-y-1">
                            {AI_CATEGORIES.map((cat) => {
                                const count = allPrompts.filter(p => p.category === cat.id).length;
                                const isSelected = selectedCategory === cat.id;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className={clsx(
                                            'w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all border',
                                            isSelected
                                                ? cat.activeColor
                                                : isDark
                                                    ? 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700'
                                                    : cat.color
                                        )}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-2">
                                                {cat.icon}
                                                <span className="truncate">{cat.id}</span>
                                            </span>
                                            <span className="text-xs opacity-75">({count})</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Suggested Queries</p>
                            {filteredPrompts.map((prompt) => (
                                <button
                                    key={prompt.id}
                                    onClick={() => handlePromptClick(prompt)}
                                    className={clsx(
                                        'w-full text-left p-2.5 rounded-lg transition-all border group',
                                        isDark
                                            ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-600 hover:border-blue-500'
                                            : 'bg-white border-slate-200 hover:bg-blue-50 hover:border-blue-400'
                                    )}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className={clsx('mt-0.5', isDark ? 'text-slate-400 group-hover:text-blue-400' : 'text-slate-600 group-hover:text-blue-600')}>
                                            {prompt.icon}
                                        </div>
                                        <div>
                                            <h4 className={clsx('font-medium text-xs mb-0.5', isDark ? 'text-white' : 'text-slate-900')}>
                                                {prompt.title}
                                            </h4>
                                            <p className={clsx('text-[10px] leading-tight', isDark ? 'text-slate-400' : 'text-slate-500')}>
                                                {prompt.description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* AGENTS TAB */}
                {activeTab === 'agents' && (
                    <div className="space-y-3">
                        {AI_AGENTS.map((agent) => (
                            <div key={agent.id} className={clsx(
                                'p-3 rounded-lg border relative group overflow-hidden',
                                isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-white border-slate-200'
                            )}>
                                <div className="flex items-start justify-between mb-2">
                                    <div className={clsx('p-2 rounded-lg', agent.bg, agent.color)}>
                                        {agent.icon}
                                    </div>
                                    <span className="px-2 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-600 rounded-full border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600">
                                        COMING SOON
                                    </span>
                                </div>
                                <h3 className={clsx('font-bold text-sm mb-1', isDark ? 'text-white' : 'text-slate-900')}>
                                    {agent.name}
                                </h3>
                                <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                                    {agent.desc}
                                </p>

                                <div className="absolute inset-0 bg-slate-900/5 backdrop-blur-[1px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Lock size={16} className="text-slate-500" />
                                </div>
                            </div>
                        ))}
                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                            <p>The <strong>Magnus AI Squad</strong> is being trained on your dataset. Agents will be deployed in the next epoch update.</p>
                        </div>
                    </div>
                )}

                {/* VISION TAB */}
                {activeTab === 'vision' && (
                    <div className="space-y-3">
                        {BLUE_SKY_FEATURES.map((feat) => (
                            <div key={feat.id} className={clsx(
                                'p-3 rounded-lg border relative overflow-hidden',
                                isDark ? 'bg-slate-800 border-amber-900/30' : 'bg-gradient-to-br from-amber-50 to-white border-amber-100'
                            )}>
                                <div className="absolute top-0 right-0 p-1">
                                    <Sparkles size={12} className="text-amber-400" />
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className={clsx('p-1.5 rounded-full', isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600')}>
                                        {feat.icon}
                                    </div>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-500">
                                        {feat.tag}
                                    </span>
                                </div>
                                <h3 className={clsx('font-bold text-sm mb-1', isDark ? 'text-white' : 'text-slate-900')}>
                                    {feat.name}
                                </h3>
                                <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                                    {feat.desc}
                                </p>
                            </div>
                        ))}
                        <div className="mt-4 text-center">
                            <p className="text-xs text-slate-400 italic">Advanced capabilities coming Q3 2026</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
